import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Building2, User, Globe, Mail } from "lucide-react";
import api from "../lib/api";
import { PageHeader, Loading, EmptyState } from "../components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

export default function Contacts() {
  const qc = useQueryClient();
  const [orgOpen, setOrgOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [org, setOrg] = useState({ name: "", industry: "", size: "", country: "", website: "" });
  const [contact, setContact] = useState({ name: "", email: "", title: "", org_name: "", phone: "" });

  const { data: orgs, isLoading } = useQuery({ queryKey: ["orgs"], queryFn: async () => (await api.get("/crm/organisations")).data });
  const { data: contacts } = useQuery({ queryKey: ["contacts"], queryFn: async () => (await api.get("/crm/contacts")).data });

  const saveOrg = useMutation({ mutationFn: (p) => api.post("/crm/organisations", p), onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["orgs"] }); setOrgOpen(false); setOrg({ name: "", industry: "", size: "", country: "", website: "" }); if (r.data.duplicate_hint) toast.warning("Possible duplicate — an organisation with this name may already exist."); else toast.success("Organisation added"); } });
  const saveContact = useMutation({ mutationFn: (p) => api.post("/crm/contacts", p), onSuccess: () => { qc.invalidateQueries({ queryKey: ["contacts"] }); setContactOpen(false); setContact({ name: "", email: "", title: "", org_name: "", phone: "" }); toast.success("Contact added"); } });

  if (isLoading) return <Loading />;

  return (
    <div>
      <PageHeader eyebrow="CRM" title="Organisations & Contacts" description="Your directory of client organisations and the people you work with." />
      <Tabs defaultValue="orgs">
        <TabsList className="mb-6">
          <TabsTrigger value="orgs" data-testid="tab-orgs">Organisations ({orgs?.length || 0})</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts ({contacts?.length || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="orgs">
          <div className="flex justify-end mb-4"><Button onClick={() => setOrgOpen(true)} data-testid="new-org" className="bg-emerald-600 hover:bg-emerald-700"><Plus className="w-4 h-4 mr-1.5" /> Add organisation</Button></div>
          {(orgs || []).length === 0 ? <EmptyState icon={Building2} title="No organisations yet" description="Add your first client organisation." /> : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {orgs.map((o) => (
                <div key={o.id} data-testid={`org-card-${o.id}`} className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5 hover:shadow-md transition-shadow">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3"><Building2 className="w-5 h-5" /></div>
                  <div className="font-display font-semibold text-slate-900">{o.name}</div>
                  <div className="text-sm text-slate-500">{o.industry || "—"}</div>
                  <div className="flex items-center gap-3 mt-3 text-xs text-slate-400">
                    {o.country && <span>{o.country}</span>}{o.size && <span>· {o.size} employees</span>}
                  </div>
                  {o.website && <div className="flex items-center gap-1.5 mt-2 text-xs text-emerald-600"><Globe className="w-3 h-3" /> {o.website}</div>}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="contacts">
          <div className="flex justify-end mb-4"><Button onClick={() => setContactOpen(true)} data-testid="new-contact" className="bg-emerald-600 hover:bg-emerald-700"><Plus className="w-4 h-4 mr-1.5" /> Add contact</Button></div>
          {(contacts || []).length === 0 ? <EmptyState icon={User} title="No contacts yet" description="Add the people you work with." /> : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {contacts.map((c) => (
                <div key={c.id} data-testid={`contact-card-${c.id}`} className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-semibold text-sm">{(c.name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("")}</div>
                    <div><div className="font-medium text-slate-900">{c.name}</div><div className="text-xs text-slate-500">{c.title}</div></div>
                  </div>
                  <div className="mt-3 space-y-1 text-sm text-slate-500">
                    {c.org_name && <div className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> {c.org_name}</div>}
                    {c.email && <div className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {c.email}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={orgOpen} onOpenChange={setOrgOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add organisation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={org.name} onChange={(e) => setOrg({ ...org, name: e.target.value })} data-testid="org-name" className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Industry</Label><Input value={org.industry} onChange={(e) => setOrg({ ...org, industry: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Country</Label><Input value={org.country} onChange={(e) => setOrg({ ...org, country: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Size</Label><Input value={org.size} onChange={(e) => setOrg({ ...org, size: e.target.value })} className="mt-1.5" placeholder="200-500" /></div>
              <div><Label>Website</Label><Input value={org.website} onChange={(e) => setOrg({ ...org, website: e.target.value })} className="mt-1.5" /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOrgOpen(false)}>Cancel</Button><Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => saveOrg.mutate(org)} disabled={!org.name} data-testid="org-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add contact</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="contact-name" className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Title</Label><Input value={contact.title} onChange={(e) => setContact({ ...contact, title: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Organisation</Label><Input value={contact.org_name} onChange={(e) => setContact({ ...contact, org_name: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Phone</Label><Input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} className="mt-1.5" /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setContactOpen(false)}>Cancel</Button><Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => saveContact.mutate(contact)} disabled={!contact.name} data-testid="contact-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
